import { Button, CircularProgress, Modal, Tooltip } from "@mui/material";
import SharedContent from "./SharedContent";
import LegContent from "./LegContent";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { useEffect, useMemo, useState } from "react";
import { EditIcon } from "../Plans/icons/GPTIcons/EditIcon";
import { InfoToolTip } from "../../assets/icons/InfoTooltip";
import usePolling from "../../hooks/usePolling";
import {
  executeOrder,
  mapTradeForPreview,
  previewOrder,
} from "../../redux/slices/tradeExecutionSlice";
import { ErrorMessage, SuccessMessage, WarningMessage } from "./InfoMessage";
import OverlayMessage from "./OverlayMessage";
import { OrderTypeSwitcher } from "../../assets/icons/TradeIdeaModal/OrderTypeSwitcher";
import { LeftArrow } from "../../assets/icons/TradeIdeaModal/LeftArrow";
import {
  constructOptionSymbols,
  capitalizeFirstLetter,
  formatDate,
  isTradeIdeaExpired,
  getOrderType,
  numberFormatter,
} from "./helpers";
import { updateTradeIdea } from "../../redux/slices/tradeIdeasSlice";
import { fetchPastSession } from "../../redux/slices/pastSessionSlice";
import { useParams } from "react-router-dom";
import { Message, Order, RootState, TradeStrategies } from "../../assets/interfaces/interfaces";
import useFetchMessages from "../../hooks/useFetchMessages";
import { calculateProfitLossForSingle } from "../PerformanceDashboard/OpenPositions";
import { ReactComponent as FilledSquareMinus } from "../../assets/icons/TradeIdeaModal/FilledSquareMinus.svg";

type ModalParentProps = {
  open: boolean;
  onClose: () => void;
  setMessages?: (value: React.SetStateAction<Record<string, Partial<Message>[]>>) => void;
  positionType?: "open" | "close";
  selectedTrade?: object;
  orderToClose?: Order;
};

export default function ModalParent({
  open,
  onClose,
  setMessages,
  positionType = "open",
  selectedTrade = null,
  orderToClose = null,
}: ModalParentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedOption: stateSelectedOption } = useSelector(
    (state: { tradeIdeas }) => state.tradeIdeas,
  );
  const selectedOption = selectedTrade || stateSelectedOption;
  console.log("selected trade", selectedOption);
  console.log("order to close", orderToClose);
  const virtualAccount = useSelector(
    (state: { tradierAccount }) => state.tradierAccount.account,
  );
  const {
    mappedTrade,
    isPreviewLoading,
    isExecuteLoading,
    error: placeOrderError,
  } = useSelector((state: { tradeOrder }) => state.tradeOrder);
  const [isPreviewed, setIsPreviewed] = useState(false);
  const [isExecuted, setIsExecuted] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [previewedOrder, setPreviewedOrder] = useState(null);
  const [showExecutedOverlay, setShowExecutedOverlay] = useState(false);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [errorType, setErrorType] = useState<
    "mapping" | "preview" | "execute" | null
  >(null);
  const [quantity, setQuantity] = useState(1);
  const [tradeSettings, setTradeSettings] = useState({
    stopLoss: 0,
    takeProfit: 0,
    limit: 0,
  });
  const [isMarket, setIsMarket] = useState(true);
  const [platformFees, setPlatformFees] = useState<number>(0);
  const legs = selectedOption?.trade_idea.legs || [];
  const isMultiLeg = legs.length > 1;
  const [updatedTradeIdea, setUpdatedTradeIdea] = useState(null);
  const { session_id } = useParams();
  const { pastMessages } = useSelector((state: RootState) => state.session);
  const user = useSelector((state: { auth }) => state.auth.currentUser);

  useEffect(() => {
    if (!selectedOption) return;
    const { trade_idea, status, created_at } = selectedOption;
    const { stop, profit, legs } = trade_idea;

    setIsPreviewed(false);
    setIsExpired(false);
    setIsExecuted(status === "traded" && positionType === "open");
    if (positionType === "close") {
      setIsMarket(trade_idea?.type === "market");
    }
    setTradeSettings({
      stopLoss: stop || 0,
      takeProfit: profit || 0,
      limit: legs[0]?.premium || 0,
    });
    setIsExpired(isTradeIdeaExpired(created_at) && positionType !== "close");
    if (
      selectedOption.preview_result
      && Object.keys(selectedOption.preview_result).length > 0
    ) {
      setPreviewedOrder(selectedOption.preview_result);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (
      previewedOrder
      && Array.isArray(previewedOrder.orders)
      && previewedOrder.orders.length > 0
    ) {
      setPlatformFees(quantity * previewedOrder.orders[0].commission);
    }
  }, [previewedOrder, quantity]);

  useEffect(() => {
    if (
      previewedOrder
      && Array.isArray(previewedOrder.orders)
      && previewedOrder.orders.length > 0
      && previewedOrder.orders[0]?.request_date
    ) {
      const checkExpiration = () => {
        const isPreviewExpired = isTradeIdeaExpired(
          previewedOrder?.orders[0]?.request_date,
        );
        if (isPreviewExpired && positionType === "open") {
          setIsExpired(true);
        }
      };

      const intervalId = setInterval(checkExpiration, 60000);

      checkExpiration();

      return () => clearInterval(intervalId);
    }
  }, [previewedOrder]);

  // polling for symbol and option quotes
  const { data } = usePolling(
    selectedOption
      ? `https://sandbox.tradier.com/v1/markets/quotes?symbols=${
          selectedOption?.trade_idea.symbol
        },${constructOptionSymbols(selectedOption?.trade_idea)}&greeks=false`
      : null,
    60000,
    virtualAccount?.access_token,
  );

  const { symbolQuote, optionQuotes } = useMemo(() => {
    if (!data || !data.quotes) {
      return { symbolQuote: null, optionQuotes: [] };
    }

    const quotesArray = data?.quotes?.quote;

    if (Array.isArray(quotesArray)) {
      const [symbolQuote = null, ...optionQuotes] = quotesArray;
      return { symbolQuote, optionQuotes };
    }

    return { symbolQuote: null, optionQuotes: [] };
  }, [data]);

  const calculateTotals = useMemo(() => {
    let totalSellPremium = 0;
    let totalBuyPremium = 0;
    let totalProceeds = 0;
    let totalCost = 0;

    if (selectedOption) {
      legs.forEach((leg, index) => {
        if (!optionQuotes || !optionQuotes[index]) return;

        const quote = optionQuotes[index];
        const midPrice = (quote.bid + quote.ask) / 2;

        const validMidPrice = Number.isNaN(midPrice) ? 0 : midPrice;

        if (leg.buy_sell === "sell") {
          if (positionType === "open") {
            totalSellPremium += leg.premium;
          }
          else {
            totalSellPremium += validMidPrice;
          }
          totalProceeds += (quote.bid * 100 * quantity) || 0;
        }
        else if (leg.buy_sell === "buy") {
          if (positionType === "open") {
            totalBuyPremium += leg.premium;
          }
          else {
            totalBuyPremium += validMidPrice;
          }
          totalCost += (quote.ask * 100 * quantity) || 0;
        }
      });
    }

    return {
      totalSellPremium,
      totalBuyPremium,
      totalProceeds,
      totalCost,
    };
  }, [legs, optionQuotes, quantity, selectedOption]);

  let netAmount = 0;
  if (positionType === "open") {
    netAmount = calculateTotals.totalSellPremium - calculateTotals.totalBuyPremium;
  }
  else {
    netAmount = calculateTotals.totalBuyPremium - calculateTotals.totalSellPremium;
  }
  const labelForNetCreditDebit = netAmount >= 0 ? "Net Credit" : "Net Debit";
  const formattedNetAmount = numberFormatter(Math.abs(netAmount));
  const formattedProceeds = numberFormatter(Math.abs(calculateTotals.totalProceeds - calculateTotals.totalCost));

  const handleTradeSettingsChange = (field, newValue) => {
    setTradeSettings(prevSettings => ({
      ...prevSettings,
      [field]: newValue,
    }));
  };

  const CustomInput = ({ value, onChange, field }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(value);

    const handleInputChange = (e) => {
      setInputValue(e.target.value);
    };

    const handleInputBlur = () => {
      setIsEditing(false);
      if (inputValue !== value) {
        onChange(field, inputValue);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleInputBlur();
      }
    };

    const handleEditClick = () => {
      setIsEditing(true);
    };

    if (isEditing) {
      return (
        <div className="flex items-center justify-between w-full">
          <input
            className="w-1/3 ml-auto"
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between w-full">
        <span className="tabular-nums">
          {value === null ? "N/A" : `$${inputValue}`}
        </span>
        <button onClick={handleEditClick}>
          <EditIcon className="fill-black ml-2 cursor-pointer" />
        </button>
      </div>
    );
  };

  const optionContentForOpenPos = [
    {
      key: "orderType",
      label: "Order Type",
      value: (() => {
        if (isPreviewed) {
          return isMarket ? "Market" : "Limit";
        }

        if (isMultiLeg) {
          return "Market";
        }

        return (
          <OrderTypeSwitcher isMarket={isMarket} setIsMarket={setIsMarket} />
        );
      })(),
    },
    { key: "timeInForce", label: "Time-in-Force", value: "Good Till Day" },
    {
      key: "strategy",
      label: "Strategy",
      value: selectedOption?.trade_idea.option_strategy,
    },
    {
      key: "platformFees",
      label: (
        <div className="flex items-center gap-x-2">
          Platform Fees
          <Tooltip
            title="Platform fees are determined by our partner broker platform"
            placement="top"
          >
            <span>
              <InfoToolTip className="cursor-pointer" />
            </span>
          </Tooltip>
        </div>
      ),
      value: numberFormatter(platformFees),
    },
    {
      key: "orderCostForSingle",
      label: (
        <p>
          {selectedOption?.trade_idea.legs[0]?.buy_sell === TradeStrategies.Buy
            ? "Order Cost"
            : "Order Proceeds"}
        </p>
      ),
      value: formattedProceeds,
    },
    {
      key: "orderCostForMulti",
      label: (
        <p>
          {labelForNetCreditDebit === "Net Credit"
            ? "Order Proceeds"
            : "Order Cost"}
        </p>
      ),
      // value: `${formatDollarsCurrency(Math.abs(proceeds)) || 0}`,
      value: formattedProceeds,
    },
    {
      key: "estimatedTotalProceeds",
      label: "Total Cost (Est.)",
      value: previewedOrder?.cost
        ? numberFormatter(previewedOrder.cost)
        : "pending",
    },
    {
      key: "breakevenPrice",
      label: "Breakeven Price",
      value: selectedOption?.trade_idea.break_even_price[0] != null
        ? numberFormatter(selectedOption.trade_idea.break_even_price[0])
        : "N/A",
    },
    {
      key: "proceedsAfterFees",
      label: "Proceeds after Fees",
      value: formattedProceeds,
    },
    {
      key: "probabilityOfProfit",
      label: "Probability of Profit",
      value: selectedOption?.trade_idea.prob_of_profit
        ? `${selectedOption.trade_idea.prob_of_profit}%`
        : "N/A",
    },
    {
      key: "maxLoss",
      label: "Max Loss",
      value: (() => {
        const maxLoss = selectedOption?.trade_idea.max_loss;

        if (maxLoss == null) {
          return "N/A";
        }

        const formattedValue = numberFormatter(maxLoss);
        return formattedValue;
      })(),
    },

    { key: "costAfterFees", label: "Cost after Fees", value: 0 },
    { key: "emptyRow", label: "", value: null },
    {
      key: "bidask", // bidask for single leg
      label: (
        <div className="flex flex-1 justify-between">
          <div className="flex gap-x-2">
            <p className="text-mui-green-text-success">Bid</p>
            <span>/</span>
            <p className="text-mui-red-success">Ask</p>
          </div>
        </div>
      ),
      value: (
        <div>
          {optionQuotes?.length > 0
            ? (
                <div className="flex items-center gap-x-2">
                  <span className="text-mui-green-text-success">
                    {numberFormatter(optionQuotes[0].bid)}
                  </span>
                  <span>/</span>
                  <span className="text-mui-red-success">
                    {numberFormatter(optionQuotes[0].ask)}
                  </span>
                </div>
              )
            : (
                <p>No quotes available</p>
              )}
        </div>
      ),
    },
    {
      key: "nettCreditOrDebit",
      label: labelForNetCreditDebit,
      value: formattedNetAmount || 0,
    },
    {
      key: "limit",
      label: "Limit",
      value: (
        <CustomInput
          value={tradeSettings.limit}
          onChange={handleTradeSettingsChange}
          field="limit"
        />
      ),
    },
    {
      key: "maxProfit",
      label: "Max Profit",
      value: (() => {
        const maxProfit = selectedOption?.trade_idea.max_profit;

        if (maxProfit == null) {
          return "N/A";
        }

        const formattedValue = numberFormatter(maxProfit);
        return formattedValue;
      })(),
    },
    {
      key: "stopLoss",
      label: (
        <div className="flex items-center gap-x-2 tabular-nums">
          Stop Loss
          <Tooltip
            title="Set automatically to 10% of order value"
            placement="top"
          >
            <span>
              <InfoToolTip className="cursor-pointer" />
            </span>
          </Tooltip>
          {!isMarket && (
            <FilledSquareMinus
              className="text-black cursor-pointer"
              onClick={() => {
                handleTradeSettingsChange("stopLoss", null);
              }}
            />
          )}
        </div>
      ),
      value: isMarket
        ? (
            "N/A"
          )
        : (
            <CustomInput
              value={tradeSettings.stopLoss}
              onChange={handleTradeSettingsChange}
              field="stopLoss"
            />
          ),
    },
    {
      key: "takeProfit",
      label: (
        <div className="flex items-center gap-x-2">
          Take Profit
          <Tooltip
            title="Set automatically to 10% of order value"
            placement="top"
          >
            <span>
              <InfoToolTip className="cursor-pointer" />
            </span>
          </Tooltip>
          {!isMarket && (
            <FilledSquareMinus
              className="text-black cursor-pointer"
              onClick={() => {
                handleTradeSettingsChange("takeProfit", null);
              }}
            />
          )}
        </div>
      ),
      value: isMarket
        ? (
            "N/A"
          )
        : (
            <CustomInput
              value={tradeSettings.takeProfit}
              onChange={handleTradeSettingsChange}
              field="takeProfit"
            />
          ),
    },
    {
      key: "premium",
      label: "Premium",
      value: selectedOption.trade_idea.legs[0]?.premium || 0,
    },
    {
      key: "action",
      label: "Action",
      value: capitalizeFirstLetter(selectedOption.trade_idea.legs[0]?.buy_sell || 0),
    },
    {
      key: "contractType",
      label: "Contract Type",
      value: capitalizeFirstLetter(selectedOption.trade_idea.legs[0]?.put_call || 0),
    },
  ];

  const optionContentForClosePos = orderToClose && [
    { key: "orderType", label: "Order Type", value: getOrderType(orderToClose?.class) },
    { key: "action", label: "Action", value: selectedOption?.trade_idea.option_strategy === TradeStrategies.Long_Call || selectedOption?.trade_idea.option_strategy === TradeStrategies.Long_Put ? "Sell to Close" : "Buy to Close" },
    { key: "timeInForce", label: "Time-in-Force", value: "Good Till Day" },
    { key: "contractType", label: "Contract Type", value: capitalizeFirstLetter(selectedOption?.trade_idea.legs[0].put_call) },
    {
      key: "platformFees",
      label: (
        <div className="flex items-center gap-x-2">
          Platform Fees
          <Tooltip title="Platform fees are determined by our partner broker platform" placement="top">
            <span>
              <InfoToolTip className="cursor-pointer" />
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "profitLoss",
      label: "P&L",
      value: isNaN(optionQuotes[0]?.bid) || isNaN(optionQuotes[0]?.ask)
        ? ""
        : numberFormatter(
          calculateProfitLossForSingle(orderToClose, (optionQuotes[0]?.bid + optionQuotes[0]?.ask) / 2),
        ),
    },
    { key: "proceedsOfClosing", label: selectedOption.trade_idea?.option_strategy === TradeStrategies.Long_Call ? "Proceeds of Closing Position" : "Cost of Closing Position", value: isMarket ? formattedProceeds : numberFormatter(tradeSettings.limit * 100) },
    {
      key: "orderCostForSingle",
      label: (
        <p>
          {selectedOption?.trade_idea.legs[0]?.buy_sell === TradeStrategies.Buy
            ? "Order Cost"
            : "Order Proceeds"}
        </p>
      ),
      value: formattedProceeds,
    },
    {
      key: "orderCostForMulti",
      label: (
        <p>
          {labelForNetCreditDebit === "Net Credit"
            ? "Order Proceeds"
            : "Order Cost"}
        </p>
      ),
      value: formattedProceeds,
    },
    {
      key: "estimatedTotalProceeds",
      label: "Total Cost (Est.)",
      value: previewedOrder
        ? `${numberFormatter(previewedOrder?.cost)}`
        : "pending",
    },
    { key: "emptyRow", label: "", value: null },
    {
      key: "nettCreditOrDebit",
      label: labelForNetCreditDebit,
      value: formattedNetAmount || 0,
    },
  ];

  const optionContent = positionType === "open" ? optionContentForOpenPos : optionContentForClosePos;

  const handleMapping = async () => {
    const defaultDuration = "day";
    const position = positionType === "open" ? "open" : "close";

    try {
      if (!quantity || quantity === undefined) {
        throw new Error("Quantity is not defined");
      }

      const updatedOption = {
        ...selectedOption.trade_idea,
        stop: isMarket ? null : tradeSettings.stopLoss,
        profit: isMarket ? null : tradeSettings.takeProfit,
        position,
        type: isMarket ? "market" : "limit",
        duration: defaultDuration,
        legs: selectedOption.trade_idea.legs.map(leg => ({
          ...leg,
          type: isMarket ? "market" : "limit",
          quantity,
          duration: defaultDuration,
          buy_sell: position === "close" && leg.buy_sell === "buy" ? "sell" : "buy",
          premium: isMarket ? leg.premium : tradeSettings.limit,
        })),
      };

      setUpdatedTradeIdea(updatedOption);
      const { payload } = await dispatch(
        mapTradeForPreview({
          provider: "tradier",
          selectedOption: updatedOption,
        }),
      );
      console.log("updated", updatedOption);

      return payload;
    }
    catch (error) {
      console.error("Error during mapTradeForPreview:", error);
    }
  };

  const handlePreview = async () => {
    try {
      const payload = await handleMapping();
      console.log("after mapped", payload);
      const transformedPayload = {
        orders: [payload],
      };

      const previewOrderResult = await dispatch(
        previewOrder({
          accountId: virtualAccount.external_id,
          order: transformedPayload,
        }),
      );

      setPreviewedOrder(previewOrderResult.payload);
      if (previewOrder.rejected.match(previewOrderResult)) {
        setErrorType("preview");
        throw new Error("Error during previewOrder dispatch");
      }
    }
    catch (error) {
      console.error("Error during previewOrder dispatch: ", error);
      setShowErrorOverlay(true);
    }
    finally {
      setIsPreviewed(true);
      setIsExpired(false);
    }
  };

  const handleExecute = async () => {
    try {
      const executeOrderResult = await dispatch(
        executeOrder({
          order: mappedTrade,
          accountId: virtualAccount.external_id,
          tradeIdeaId: selectedOption.id,
          vendor: "tradier",
        }),
      );

      if (executeOrder.rejected.match(executeOrderResult)) {
        setErrorType("execute");
        throw new Error("Error during previewOrder dispatch");
      }

      if (
        executeOrderResult.meta.requestStatus === "fulfilled"
        && placeOrderError === null
      ) {
        setShowExecutedOverlay(true);
        setIsExecuted(true);
        // update changes made to trade idea (SLTP, quantity, limit) + save preview costs
        await dispatch(
          updateTradeIdea({
            previewedOrder,
            updatedTradeIdea,
            tradeIdeaId: selectedOption.id,
          }),
        );
      }
    }
    catch (error) {
      console.error("Error executing order: ", error);
      setShowErrorOverlay(true);
    }
  };

  const sessionIdSafe = session_id;

  const fetchMessages = useFetchMessages(
    sessionIdSafe,
    user?.userId,
    user?.accessToken,
    setMessages,
  );

  const handleRegenerateMessage = async () => {
    await dispatch(fetchPastSession({ sessionId: session_id }));
    const foundMessage = pastMessages.find(
      message => message.message_id === selectedOption.message_id,
    );
    if (foundMessage) {
      fetchMessages(foundMessage.user_query, "user_type");
    }
  };
  // logic for showing success, error messages, and appropriate buttons
  const shouldShowPreviewSuccess
    = isPreviewed
    && !isExecuted
    && !isExpired
    && !isPreviewLoading
    && !placeOrderError;
  const shouldShowError
    = (isPreviewed || isExecuted) && placeOrderError && errorType;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow w-modal-lg text-mui-black-87">
          {showExecutedOverlay && (
            <OverlayMessage setShowExecutedOverlay={setShowExecutedOverlay} />
          )}
          {showErrorOverlay && (
            <OverlayMessage
              setShowErrorOverlay={setShowErrorOverlay}
              errorMessage={placeOrderError.error}
              errorType={errorType}
            />
          )}
          <section className="flex flex-col gap-y-4 bg-mui-blue-gray p-6 rounded-lg pb-0 rounded-b-none">
            <SharedContent
              onClose={onClose}
              optionContent={optionContent}
              quantity={quantity}
              setQuantity={setQuantity}
              isPreviewed={isPreviewed}
              symbolQuote={symbolQuote}
              orderToClose={orderToClose}
              selectedTrade={selectedOption}
            />
          </section>
          <section className="bg-white p-6 rounded-b-lg flex flex-col gap-y-6">
            <LegContent
              optionContent={optionContent}
              optionQuotes={optionQuotes}
              isMarket={isMarket}
              quantity={quantity}
              selectedTrade={selectedOption}
              isClosePosition={positionType === "close"}
            />
            <div className="flex flex-col items-center gap-y-4">
              {!isPreviewed && !isExecuted && !isExpired && (
                <div className="w-full sm:w-80 self-center">
                  <Button
                    variant="contained"
                    size="large"
                    className="tradesButton-submit"
                    disabled={isPreviewLoading}
                    onClick={handlePreview}
                    fullWidth
                  >
                    {isPreviewLoading
                      ? (
                          <div className="flex items-center">
                            <CircularProgress color="inherit" size={25} />
                          </div>
                        )
                      : (
                          "Preview"
                        )}
                  </Button>
                </div>
              )}
              {shouldShowPreviewSuccess && (
                <div className="w-full flex flex-col gap-y-6">
                  <SuccessMessage message="Ready to execute" />
                  <div className="flex gap-x-6">
                    <Button
                      variant="outlined"
                      size="large"
                      color="inherit"
                      className="tradesButton-outlined"
                      fullWidth
                      onClick={() => setIsPreviewed(false)}
                    >
                      <LeftArrow className="me-2" />
                      Modify
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      className="green-submit"
                      disabled={isExecuteLoading}
                      onClick={handleExecute}
                      fullWidth
                    >
                      {isExecuteLoading
                        ? (
                            <div className="flex items-center">
                              <CircularProgress color="inherit" size={25} />
                            </div>
                          )
                        : (
                            "Execute"
                          )}
                    </Button>
                  </div>
                </div>
              )}
              {shouldShowError && (
                <div className="w-full flex flex-col gap-y-6">
                  <ErrorMessage message="Unable to execute" />
                  <div className="w-full sm:w-80 self-center">
                    <Button
                      variant="contained"
                      size="large"
                      className="tradesButton-submit"
                      onClick={() => setIsPreviewed(false)}
                      fullWidth
                    >
                      <LeftArrow className="pe-2" />
                      Modify
                    </Button>
                  </div>
                </div>
              )}

              {isExecuted && (
                <div className="w-full flex flex-col gap-y-6">
                  <WarningMessage
                    message={`This trade has already been executed on ${formatDate(
                      selectedOption?.preview_result.request_date || new Date(),
                    )}.`}
                  />
                </div>
              )}
              {isExpired && !isExecuted && !isPreviewed && (
                <div className="w-full flex flex-col gap-y-6">
                  <WarningMessage message="This trade idea has expired" />
                  <div className="flex gap-x-6 w-full">
                    <Button
                      variant="outlined"
                      size="large"
                      color="inherit"
                      className="tradesButton-outlined"
                      fullWidth
                      onClick={onClose}
                    >
                      <LeftArrow className="me-2" />
                      Cancel
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      className="tradesButton-outlined"
                      disabled={isPreviewLoading}
                      onClick={handleRegenerateMessage}
                      fullWidth
                    >
                      Regenerate
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      className="tradesButton-submit button-loading"
                      disabled={isPreviewLoading}
                      onClick={handlePreview}
                      fullWidth
                    >
                      {isPreviewLoading
                        ? (
                            <div className="flex items-center">
                              <CircularProgress color="inherit" size={25} />
                            </div>
                          )
                        : (
                            "Continue to preview"
                          )}
                    </Button>
                  </div>
                </div>
              )}
              {isExpired && isPreviewed && (
                <div className="w-full flex flex-col gap-y-6">
                  <WarningMessage message="The preview of this trade idea has expired." />
                  <div className="flex gap-x-6 w-full">
                    <Button
                      variant="contained"
                      size="large"
                      className="tradesButton-submit"
                      onClick={() => setIsPreviewed(false)}
                      fullWidth
                    >
                      <LeftArrow className="pe-2" />
                      Modify
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      className="green-submit"
                      onClick={handleExecute}
                      fullWidth
                    >
                      {isExecuteLoading
                        ? (
                            <div className="flex items-center">
                              <CircularProgress color="inherit" size={25} />
                            </div>
                          )
                        : (
                            "Execute"
                          )}
                    </Button>
                  </div>
                </div>
              )}

              <Tooltip
                title="Market data changes in real time, and is only finalized at execution"
                placement="top"
              >
                <div className="caption">
                  Why are some values different when I preview/execute?
                </div>
              </Tooltip>
            </div>
          </section>
        </div>
      </Modal>
    </>
  );
}
